// Wspólna obsługa dat.
//
// Wcześniej istniały dwie niezależne, ale identyczne co do logiki funkcje:
// `parseContainerDate` w Dashboard.js i `parseFlexibleDate` w StockManagement.js.
// Rozjechanie się ich w przyszłości oznaczałoby, że pulpit i raport kosztowy
// liczą różne rzeczy z tych samych danych — dlatego jest tu jedna definicja.

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Rozpoznaje datę z dowolnego formatu, jaki może trafić z Firestore:
 * stringa ISO, obiektu Date, Timestampu ({ seconds } lub z metodą .toDate()).
 * Zwraca `null` zamiast "Invalid Date", żeby błędne wartości dało się wykryć
 * i zgłosić, a nie cicho policzyć jako NaN.
 */
export const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      const date = value.toDate();
      return Number.isNaN(date.getTime()) ? null : date;
    }
    if (typeof value.seconds === 'number') {
      return new Date(value.seconds * 1000);
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Normalizuje datę do północy UTC. Wszystkie obliczenia "dni składowania"
 * operują na dniach kalendarzowych — bez tego zmiana czasu letniego/zimowego
 * potrafi dodać lub odjąć dzień w rozliczeniu.
 */
export const toUtcMidnight = (value) => {
  const date = parseDate(value);
  if (!date) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};

export const addDays = (date, days) => {
  const result = new Date(date);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
};

/** Różnica w pełnych dniach między dwiema datami (znormalizowanymi do UTC). */
export const daysBetween = (from, to) => {
  const start = toUtcMidnight(from);
  const end = toUtcMidnight(to);
  if (!start || !end) return null;
  return Math.round((end.getTime() - start.getTime()) / DAY_MS);
};

/** Zakres miesiąca "YYYY-MM": start włącznie, end wyłącznie (1. dzień kolejnego). */
export const getMonthRange = (monthString) => {
  const [year, month] = monthString.split('-').map(Number);
  return {
    start: new Date(Date.UTC(year, month - 1, 1)),
    end: new Date(Date.UTC(year, month, 1)),
  };
};

export const isDateInMonth = (value, monthString) => {
  if (!value || !monthString) return false;
  const date = toUtcMidnight(value);
  if (!date) return false;
  const { start, end } = getMonthRange(monthString);
  return date >= start && date < end;
};

/** Bieżący miesiąc jako "YYYY-MM" — wg czasu lokalnego, nie UTC. */
export const currentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Klucz dnia "YYYY-MM-DD" wg czasu LOKALNEGO — używany do grupowania zdarzeń
 * na wykresie ruchu dziennego. Celowo lokalny, nie UTC: operator patrzący na
 * wykres myśli o dniach w swojej strefie, a nie w Greenwich.
 */
export const localDateKey = (value) => {
  const date = parseDate(value);
  if (!date) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const dateFormatter = new Intl.DateTimeFormat('pl-PL', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('pl-PL', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const shortDateFormatter = new Intl.DateTimeFormat('pl-PL', {
  day: '2-digit',
  month: '2-digit',
});

/** Formatowanie do wyświetlenia. `fallback` wraca, gdy daty nie da się rozpoznać. */
export const formatDate = (value, fallback = '—') => {
  const date = parseDate(value);
  return date ? dateFormatter.format(date) : fallback;
};

export const formatDateTime = (value, fallback = '—') => {
  const date = parseDate(value);
  return date ? dateTimeFormatter.format(date) : fallback;
};

export const formatShortDate = (value, fallback = '—') => {
  const date = parseDate(value);
  return date ? shortDateFormatter.format(date) : fallback;
};

/** Dla <input type="date"> — wymaga formatu "YYYY-MM-DD". */
export const toDateInputValue = (value) => localDateKey(value) || '';
